import { createAsyncThunk } from '@reduxjs/toolkit';
import { OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import getSaleGroup from '../utils/getSaleGroup';

import debug from '../utils/debug';

import type { RootState } from 'store';
import { DeliveryAddressDto } from '../libs/polygon-ordering/src/dtos/DeliveryAddressDto';
import { setDisableFetchDeliveryFeeApi } from '../slices/disableFetchDeliveryFeeApi';

const { getMember, getDeliverySurcharges, getDeliveryFee, getSaleType } = OrderingSelectors;
const { fetchDeliveryFee, setDeliveryFee, setDeliverySurcharges } = OrderingOperations;

export const deliveryFee = createAsyncThunk(
  'deliveryFee',
  async (_data: undefined, { dispatch, getState, rejectWithValue }) => {
    debug('deliveryFee');

    try {
      const memberPresent = Boolean(getMember(getState() as RootState));
      const effectiveDeliveryAddress = (getState() as RootState).desiredDeliveryAddress;
      const saleType = getSaleType(getState() as RootState);
      const { isDelivery } = getSaleGroup(saleType);

      const deliveryAddressString =
        DeliveryAddressDto.toShortAddressString(effectiveDeliveryAddress);

      let fetchResult: DeliveryFee | null = null;

      try {
        fetchResult = await dispatch(
          fetchDeliveryFee({
            authenticationMethod: memberPresent ? 'member' : 'none',
            deliveryDropOffCoordinates: effectiveDeliveryAddress?.deliveryDropOffCoordinates,
            customAddress: deliveryAddressString,
            customSuburb: effectiveDeliveryAddress?.addressComponents?.suburb,
            customPostCode: effectiveDeliveryAddress?.addressComponents?.postCode,
          }),
        ).unwrap();
        dispatch(setDeliveryFee(fetchResult));
      } catch (e: any) {
        const message = typeof e?.e?.message === 'string' ? e.e.message : '';
        const errorType = e?.e?.details?.type;
        if (errorType === 'http' || errorType === 'connectivity') {
          dispatch(setDisableFetchDeliveryFeeApi(true));
        }
        throw new Error(message || 'delivery fee failed');
      }

      const deliverySurcharges = getDeliverySurcharges(getState() as RootState);
      const deliveryFeeData = getDeliveryFee(getState() as RootState);
      const isDeliveryCampaign = isDelivery && deliveryFeeData?.deliveryFee;
      // if the discounted delivery fee exists, then we want to overwrite the
      // delivery fee surcharge with the discounted delivery fee
      const newDeliveryFeeAmount =
        deliveryFeeData && deliveryFeeData?.discountedDeliveryFee === undefined
          ? deliveryFeeData?.deliveryFee
          : deliveryFeeData?.discountedDeliveryFee;
      const checkDeliveryFeeUpdateCondition =
        deliverySurcharges?.[0]?.amount !== newDeliveryFeeAmount;

      if (isDeliveryCampaign && checkDeliveryFeeUpdateCondition) {
        dispatch(
          setDeliverySurcharges([
            {
              description: deliverySurcharges?.[0]?.description as string,
              amount: newDeliveryFeeAmount as number,
              type: deliverySurcharges?.[0]?.type as number,
            },
          ]),
        );
      }
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
